import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

/**
 * A page's claim on the whole window.
 *
 * A nook is a place you sit in, not a page under the head's chrome: while one
 * is open the top bar and the shell's writing panel step away, and the page
 * owns every edge — including its own writing. Released on the way out, so
 * the next page gets its chrome back.
 */
const FullBleedContext = createContext<{ on: boolean; set: (on: boolean) => void }>({
  on: false,
  set: () => {},
});

export function FullBleedProvider({ children }: { children: ReactNode }) {
  const [on, set] = useState(false);
  const value = useMemo(() => ({ on, set }), [on]);
  return <FullBleedContext.Provider value={value}>{children}</FullBleedContext.Provider>;
}

export function useFullBleed(): boolean {
  return useContext(FullBleedContext).on;
}

export function useProvideFullBleed(): void {
  const { set } = useContext(FullBleedContext);
  useEffect(() => {
    set(true);
    return () => set(false);
  }, [set]);
}
